import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom';
import { AppShell } from './screens/layouts/AppShell';
import { MobSplash } from './components/mob';
import { useSession } from './auth/useSession';

const TodayScreen = lazy(() => import('./screens/today/TodayScreen'));
const GuestsScreen = lazy(() => import('./screens/guests/GuestsScreen'));
const GuestDetailScreen = lazy(() => import('./screens/guests/GuestDetailScreen'));
const MealsScreen = lazy(() => import('./screens/meals/MealsScreen'));
const OccupancyScreen = lazy(() => import('./screens/occupancy/OccupancyScreen'));
const ShortageScreen = lazy(() => import('./screens/shortage/ShortageScreen'));
const ApprovalsScreen = lazy(() => import('./screens/approvals/ApprovalsScreen'));
const ApprovalDetailScreen = lazy(() => import('./screens/approvals/ApprovalDetailScreen'));
const QuickBookingScreen = lazy(() => import('./screens/quick-booking/QuickBookingScreen'));
const MisScreen = lazy(() => import('./screens/mis/MisScreen'));
const LoginScreen = lazy(() => import('./screens/auth/LoginScreen'));
const PropertyPickerScreen = lazy(() => import('./screens/auth/PropertyPickerScreen'));

function LazyBoundary() {
  return (
    <Suspense fallback={<MobSplash />}>
      <Outlet />
    </Suspense>
  );
}

function ProtectedGate() {
  const { isAuthenticated, isBootstrapping } = useSession();
  const location = useLocation();
  if (isBootstrapping) return <MobSplash />;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Outlet />;
}

function PublicGate() {
  const { isAuthenticated, isBootstrapping } = useSession();
  if (isBootstrapping) return <MobSplash />;
  if (isAuthenticated) return <Navigate to="/today" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <LazyBoundary />,
    children: [
      {
        element: <PublicGate />,
        children: [{ path: '/login', element: <LoginScreen /> }],
      },
      {
        element: <ProtectedGate />,
        children: [
          { path: '/property', element: <PropertyPickerScreen /> },
          {
            element: <AppShell />,
            children: [
              { index: true, element: <Navigate to="/today" replace /> },
              { path: 'today', element: <TodayScreen /> },
              {
                path: 'guests',
                element: <Navigate to="/guests/arrivals" replace />,
              },
              { path: 'guests/:tab', element: <GuestsScreen /> },
              { path: 'guests/:tab/:bookingId', element: <GuestDetailScreen /> },
              { path: 'meals', element: <MealsScreen /> },
              { path: 'occupancy', element: <OccupancyScreen /> },
              { path: 'shortage', element: <ShortageScreen /> },
              { path: 'approvals', element: <ApprovalsScreen /> },
              { path: 'approvals/:id', element: <ApprovalDetailScreen /> },
              { path: 'quick-booking', element: <QuickBookingScreen /> },
              { path: 'mis', element: <MisScreen /> },
            ],
          },
        ],
      },
    ],
  },
]);
